import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { UserPlus, Loader2, ShieldCheck, User, KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "./OfficerHome";
import { useAuth } from "../context/AuthContext";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [add, setAdd] = useState(false);
  const [reset, setReset] = useState(null);
  const [del, setDel] = useState(null);
  const { user: me } = useAuth();
  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (u, field) => {
    try {
      await api.patch(`/users/${u.id}`, { [field]: !u[field] });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-head text-2xl font-bold">Users</h1>
        <button className="btn-gold flex items-center gap-2" data-testid="add-user-btn" onClick={() => setAdd(true)}><UserPlus className="h-4 w-4" /> Tambah</button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {users.map((u) => (
          <div key={u.id} className="nnx-card p-4" data-testid={`user-${u.email}`}>
            <div className="flex items-center gap-2">
              {u.role === "owner" ? <ShieldCheck className="h-5 w-5 text-[#EAB308]" /> : <User className="h-5 w-5 text-[#94A3B8]" />}
              <div>
                <div className="font-semibold">{u.name}</div>
                <div className="text-xs text-[#64748B]">{u.email}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3 text-xs">
              <span className="px-2 py-0.5 rounded bg-[#222B3D] uppercase tracking-widest">{u.role}</span>
            </div>
            <div className="mt-3 space-y-2 text-sm">
              <Toggle label="Aktif" on={u.active} onClick={() => toggle(u, "active")} testid={`toggle-active-${u.email}`} />
              {u.role === "officer" && <Toggle label="Izin Custom Rate" on={u.allow_custom_rate} onClick={() => toggle(u, "allow_custom_rate")} testid={`toggle-custom-${u.email}`} />}
              <button data-testid={`reset-pwd-${u.email}`} onClick={() => setReset(u)} className="flex items-center gap-1.5 text-xs text-[#EAB308] hover:underline mt-1">
                <KeyRound className="h-3.5 w-3.5" /> Reset Password
              </button>
              {me?.id !== u.id && (
                <button data-testid={`del-user-${u.email}`} onClick={() => setDel(u)} className="flex items-center gap-1.5 text-xs text-rose-400 hover:underline mt-1">
                  <Trash2 className="h-3.5 w-3.5" /> Hapus Akun
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {add && <AddUser onClose={() => setAdd(false)} onDone={() => { setAdd(false); load(); }} />}
      {reset && <ResetPwd user={reset} onClose={() => setReset(null)} onDone={() => setReset(null)} />}
      {del && <DeleteUser user={del} onClose={() => setDel(null)} onDone={() => { setDel(null); load(); }} />}
    </div>
  );
}

function DeleteUser({ user, onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      await api.delete(`/users/${user.id}`);
      toast.success(`Akun ${user.name} dihapus`);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  return (
    <Modal title={`Hapus ${user.name}?`} onClose={onClose} testid="delete-user-modal">
      <p className="text-sm text-[#94A3B8] mb-4">Akun <b className="text-white">{user.email}</b> akan dihapus permanen. Riwayat transaksinya tetap tersimpan untuk audit.</p>
      <button className="btn-gold w-full bg-rose-500 hover:bg-rose-600 text-white" data-testid="confirm-delete-user" onClick={submit} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "HAPUS AKUN"}</button>
    </Modal>
  );
}

function ResetPwd({ user, onClose, onDone }) {
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (pwd.length < 6) { toast.error("Password minimal 6 karakter"); return; }
    setLoading(true);
    try {
      await api.patch(`/users/${user.id}`, { password: pwd });
      toast.success(`Password ${user.name} direset`);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  return (
    <Modal title={`Reset Password — ${user.name}`} onClose={onClose} testid="reset-pwd-modal">
      <label className="label">Password Baru</label>
      <input data-testid="reset-pwd-input" type="password" className="nnx-input mt-1" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="min. 6 karakter" />
      <button className="btn-gold w-full mt-4" data-testid="reset-pwd-submit" onClick={submit} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "RESET PASSWORD"}</button>
    </Modal>
  );
}

function Toggle({ label, on, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="flex items-center justify-between w-full">
      <span className="text-[#94A3B8]">{label}</span>
      <span className={`w-10 h-5 rounded-full transition-colors relative ${on ? "bg-emerald-500" : "bg-[#222B3D]"}`}>
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${on ? "left-5" : "left-0.5"}`} />
      </span>
    </button>
  );
}

function AddUser({ onClose, onDone }) {
  const [f, setF] = useState({ name: "", email: "", password: "", role: "officer", allow_custom_rate: false });
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try { await api.post("/users", f); toast.success("User dibuat"); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  return (
    <Modal title="Tambah User" onClose={onClose} testid="add-user-modal">
      <div className="space-y-3">
        <div><label className="label">Nama</label><input data-testid="user-name" className="nnx-input mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div><label className="label">Email</label><input data-testid="user-email" type="email" className="nnx-input mt-1" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
        <div><label className="label">Password</label><input data-testid="user-password" type="password" className="nnx-input mt-1" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
        <div><label className="label">Role</label>
          <select data-testid="user-role" className="nnx-input mt-1" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
            <option value="officer">Officer</option><option value="owner">Owner</option>
          </select>
        </div>
      </div>
      <button className="btn-gold w-full mt-4" data-testid="save-user" onClick={submit} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "BUAT USER"}</button>
    </Modal>
  );
}
